/**
 * CL-DISC-02 — the listing discount rules, shared by the browser and the Express server.
 *
 * Deliberately CommonJS with ZERO dependencies, for the same reason as shippingRules.js:
 * `server/` cannot import ESM from `src/`, and the webpack build's ModuleScopePlugin forbids a
 * client import from outside `src/`. So the key names, the arithmetic and the validation live
 * here exactly once, and both the form (discountSettings.js) and the sweep
 * (server/api-util/custom/listingDiscount.js) require this file. A second spelling of a key
 * name in either tree is the bug this file exists to prevent.
 *
 * Amounts are integer currency subunits. `Math.round` on an integer product is exact half-up
 * rounding, so no Decimal dependency is needed here.
 */

// Spec §B. Flat, top-level publicData keys (D6): the search schema indexes only top-level keys,
// and only `discountActive` is registered (spec §I).
const DISCOUNT_KEYS = Object.freeze({
  active: 'discountActive',
  percentage: 'discountPercentage',
  originalPrice: 'discountOriginalPriceInSubunits',
  endsAt: 'discountEndsAt',
  maxUses: 'discountMaxUses',
  usesCount: 'discountUsesCount',
  startedAt: 'discountStartedAt',
});

// Sparse-fieldset paths for listing queries that request `fields.listing` (e.g. the search
// page). Without them the card never sees the discount and shows only the sale price.
const DISCOUNT_LISTING_FIELDS = Object.freeze(
  Object.values(DISCOUNT_KEYS).map(key => `publicData.${key}`)
);

const MIN_PERCENTAGE = 1;
const MAX_PERCENTAGE = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

// Spec §F. Every transition in clastique-purchase/process.edn whose :to is a state where the
// buyer keeps the goods and the seller keeps the money. A transaction whose LAST transition is
// one of these is a completed order. Cancels and expiries are deliberately absent: a refunded
// order stops counting, which is the intended behaviour (spec §F, AC11a).
const COMPLETED_TRANSITIONS = Object.freeze([
  'transition/confirm-payment',
  'transition/mark-received-from-purchased',
  'transition/mark-delivered',
  'transition/operator-mark-delivered',
  'transition/update-tracking',
  'transition/mark-received',
  'transition/auto-mark-received',
  'transition/dispute',
  'transition/operator-dispute',
  'transition/mark-received-from-disputed',
  'transition/auto-complete',
  'transition/review-1-by-provider',
  'transition/review-2-by-provider',
  'transition/review-1-by-customer',
  'transition/review-2-by-customer',
  'transition/expire-review-period',
  'transition/expire-provider-review-period',
  'transition/expire-customer-review-period',
]);

const isIsoInstant = s => typeof s === 'string' && !Number.isNaN(Date.parse(s));
const isPercentage = n => Number.isInteger(n) && n >= MIN_PERCENTAGE && n <= MAX_PERCENTAGE;
const isMaxUses = n => Number.isInteger(n) && n >= 1;

/**
 * The discount on a listing, or null. Null means "treat as no discount" — including when the
 * keys are present but malformed (EC5): the sweep must never restore a price it cannot trust,
 * and the display must never strike through a number that was never a real price.
 */
const readListingDiscount = publicData => {
  if (!publicData || publicData[DISCOUNT_KEYS.active] !== true) return null;
  const percentage = publicData[DISCOUNT_KEYS.percentage];
  const originalPriceInSubunits = publicData[DISCOUNT_KEYS.originalPrice];
  if (!isPercentage(percentage)) return null;
  if (!Number.isInteger(originalPriceInSubunits) || originalPriceInSubunits <= 0) return null;

  const endsAtRaw = publicData[DISCOUNT_KEYS.endsAt];
  const maxUsesRaw = publicData[DISCOUNT_KEYS.maxUses];
  const endsAt = isIsoInstant(endsAtRaw) ? endsAtRaw : null;
  const maxUses = isMaxUses(maxUsesRaw) ? maxUsesRaw : null;
  if (!endsAt && maxUses == null) return null; // SL4

  const usesRaw = publicData[DISCOUNT_KEYS.usesCount];
  const usesCount = Number.isInteger(usesRaw) && usesRaw >= 0 ? usesRaw : 0;
  const startedAtRaw = publicData[DISCOUNT_KEYS.startedAt];
  const startedAt = isIsoInstant(startedAtRaw) ? startedAtRaw : null;

  return { percentage, originalPriceInSubunits, endsAt, maxUses, usesCount, startedAt };
};

/** Spec §D: salePrice = round(regular × (100 − percentage) / 100), half up. */
const saleFromRegular = (regularInSubunits, percentage) =>
  Math.round((regularInSubunits * (100 - percentage)) / 100);

/**
 * SL1–SL5, in that order; returns the FIRST failing rule's code or null. One definition, so the
 * form's field validators and anything server-side refuse the same inputs for the same reasons.
 */
const validateDiscountInput = ({
  regularInSubunits,
  percentage,
  endsAtIso,
  maxUses,
  nowMs,
  minimumPriceSubunits = 0,
}) => {
  if (!isPercentage(percentage)) return 'percentage-range';
  if (
    Number.isInteger(regularInSubunits) &&
    saleFromRegular(regularInSubunits, percentage) < minimumPriceSubunits
  ) {
    return 'below-minimum';
  }
  if (endsAtIso != null && (!isIsoInstant(endsAtIso) || Date.parse(endsAtIso) <= nowMs)) {
    return 'end-date-past';
  }
  const hasEnd = endsAtIso != null;
  const hasMax = maxUses != null;
  if (!hasEnd && !hasMax) return 'no-limit';
  if (hasMax && !isMaxUses(maxUses)) return 'max-uses-invalid';
  return null;
};

/** Spec §B: ending a discount sets every key to null, which deletes it. */
const clearedDiscountPublicData = () =>
  Object.values(DISCOUNT_KEYS).reduce((acc, key) => ({ ...acc, [key]: null }), {});

/** Spec §E step 3. */
const isDiscountOver = (discount, nowMs) => {
  const byDate = !!discount.endsAt && Date.parse(discount.endsAt) <= nowMs;
  const byUses = discount.maxUses != null && discount.usesCount >= discount.maxUses;
  return byDate || byUses;
};

/**
 * Spec §G. `daysLeft` is WHOLE days until `endsAt`: on the last day it is 0 (rendered "today"),
 * the day before it is 1 ("tomorrow"). With both limits set, the scarcer one is shown — uses
 * left against days left counted inclusively — and a tie goes to uses, which cannot be waited
 * out.
 */
const discountUrgency = (discount, nowMs) => {
  const byDate = discount.endsAt
    ? {
        kind: 'date',
        daysLeft: Math.max(0, Math.floor((Date.parse(discount.endsAt) - nowMs) / DAY_MS)),
      }
    : null;
  const byUses =
    discount.maxUses != null
      ? { kind: 'uses', usesLeft: Math.max(0, discount.maxUses - discount.usesCount) }
      : null;
  if (byDate && byUses) return byUses.usesLeft <= byDate.daysLeft + 1 ? byUses : byDate;
  return byDate || byUses;
};

module.exports = {
  DISCOUNT_KEYS,
  DISCOUNT_LISTING_FIELDS,
  MIN_PERCENTAGE,
  MAX_PERCENTAGE,
  COMPLETED_TRANSITIONS,
  readListingDiscount,
  saleFromRegular,
  validateDiscountInput,
  clearedDiscountPublicData,
  isDiscountOver,
  discountUrgency,
};
